import { useState, useEffect, useMemo, useRef } from "react";
import { X, Search, Plus, Trash2, ChefHat, Package, AlertCircle, DollarSign } from "lucide-react";
import type { InventoryItem } from "../../types/inventory";
import type { RecipeCostAnalysis } from "./configurationTypes";
import { analyzeRecipe } from "./configurationUtils";
import { Button } from "../ui/button";

interface RecipeIngredient {
  itemId: string;
  nom: string;
  quantite: number;
  unite: string;
}

interface RecipeConfigModalProps {
  isOpen: boolean;
  onClose: () => void;
  recipe: InventoryItem;
  availableIngredients: InventoryItem[];
  allItems: InventoryItem[];
  onSave: (recipeId: string, composition: RecipeIngredient[]) => void;
}

export function RecipeConfigModal({
  isOpen,
  onClose,
  recipe,
  availableIngredients,
  allItems,
  onSave,
}: RecipeConfigModalProps) {
  const [composition, setComposition] = useState<RecipeIngredient[]>(
    recipe.composition || []
  );
  const [searchTerm, setSearchTerm] = useState("");
  
  // Store initial composition to detect changes
  const initialComposition = useRef<RecipeIngredient[]>(recipe.composition || []);

  // Check if composition has been modified
  const hasModifications = useMemo(() => {
    if (composition.length !== initialComposition.current.length) return true;
    
    // Deep comparison of composition arrays
    return composition.some((item, index) => {
      const initial = initialComposition.current[index];
      if (!initial) return true;
      
      return (
        item.itemId !== initial.itemId ||
        item.quantite !== initial.quantite ||
        item.unite !== initial.unite
      );
    });
  }, [composition]);

  // Filtrer les ingrédients disponibles
  const filteredIngredients = useMemo(() => {
    if (!searchTerm) return availableIngredients;
    
    const term = searchTerm.toLowerCase();
    return availableIngredients.filter(
      (item) =>
        item.nom.toLowerCase().includes(term) ||
        item.categorie.toLowerCase().includes(term)
    );
  }, [availableIngredients, searchTerm]);

  // Calcul en temps réel
  const analysis: RecipeCostAnalysis = useMemo(() => {
    const tempRecipe = { ...recipe, composition };
    return analyzeRecipe(tempRecipe, allItems);
  }, [composition, recipe, allItems]);

  const handleAddIngredient = (ingredient: InventoryItem) => {
    // Vérifier si déjà ajouté
    const exists = composition.find((c) => c.itemId === ingredient.id);
    if (exists) return;

    const newItem: RecipeIngredient = {
      itemId: ingredient.id,
      nom: ingredient.nom,
      quantite: 1,
      unite: "ml",
    };

    setComposition([...composition, newItem]);
    setSearchTerm(""); // Clear search après ajout
  };

  const handleRemoveIngredient = (index: number) => {
    setComposition(composition.filter((_, i) => i !== index));
  };

  const handleUpdateQuantity = (index: number, quantite: number) => {
    const updated = [...composition];
    updated[index] = { ...updated[index], quantite };
    setComposition(updated);
  };

  const handleUpdateUnit = (index: number, unite: string) => {
    const updated = [...composition];
    updated[index] = { ...updated[index], unite };
    setComposition(updated);
  };

  const handleSave = () => {
    onSave(recipe.id, composition);
    onClose();
  };

  // Handle backdrop click - close only if no modifications
  const handleBackdropClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget && !hasModifications) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
      onClick={handleBackdropClick}
    >
      <div className="bg-[var(--card)] rounded-xl border border-[var(--border)] shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-[var(--border)]">
          <div>
            <h2 className="text-xl font-bold text-[var(--foreground)]">
              Recette : {recipe.nom}
            </h2>
            <p className="text-sm text-[var(--muted-foreground)] mt-1">
              Définissez les ingrédients de cet article du menu
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-[var(--muted)] rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-[var(--muted-foreground)]" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Colonne gauche : Recherche et ajout */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-[var(--muted-foreground)] mb-2">
                  Ajouter des ingrédients
                </label>
                
                {/* Barre de recherche */}
                <div className="relative mb-3">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[var(--muted-foreground)]" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Rechercher un ingrédient..."
                    className="w-full pl-10 pr-4 py-2 bg-[var(--input-background)] border border-[var(--border)] rounded-lg text-[var(--foreground)] placeholder-[var(--muted-foreground)] focus:outline-none focus:border-[var(--color-steel)]"
                  />
                </div>

                {/* Liste des ingrédients disponibles */}
                <div className="border border-[var(--border)] rounded-lg max-h-[400px] overflow-y-auto">
                  {filteredIngredients.length === 0 ? (
                    <div className="p-8 text-center text-[var(--muted-foreground)]">
                      <Package className="w-12 h-12 mx-auto mb-2 opacity-30" />
                      <p className="text-sm">Aucun ingrédient trouvé</p>
                    </div>
                  ) : (
                    filteredIngredients.map((ingredient) => {
                      const alreadyAdded = composition.find(
                        (c) => c.itemId === ingredient.id
                      );

                      return (
                        <button
                          key={ingredient.id}
                          onClick={() => handleAddIngredient(ingredient)}
                          disabled={!!alreadyAdded}
                          className={`
                            w-full flex items-center justify-between p-3 border-b border-[var(--border)] last:border-b-0 transition-colors text-left
                            ${
                              alreadyAdded
                                ? "opacity-40 cursor-not-allowed bg-[var(--muted)]"
                                : "hover:bg-[var(--muted)] cursor-pointer"
                            }
                          `}
                        >
                          <div className="flex-1">
                            <p className="text-sm font-medium text-[var(--foreground)]">
                              {ingredient.nom}
                            </p>
                            <p className="text-xs text-[var(--muted-foreground)]">
                              {ingredient.categorie}
                              {ingredient.prixCoutant > 0 && (
                                <span className="ml-2">
                                  • {ingredient.prixCoutant.toFixed(2)}$
                                  {ingredient.formatQuantite && ingredient.formatUnite && (
                                    <span> / {ingredient.formatQuantite}{ingredient.formatUnite}</span>
                                  )}
                                </span>
                              )}
                            </p>
                          </div>
                          {alreadyAdded && (
                            <span className="text-xs text-[var(--color-steel)] font-medium">
                              Ajouté
                            </span>
                          )}
                        </button>
                      );
                    })
                  )}
                </div>
              </div>
            </div>

            {/* Colonne droite : Composition actuelle */}
            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <label className="block text-sm font-medium text-[var(--muted-foreground)]">
                  Recette ({composition.length} ingrédient{composition.length > 1 ? 's' : ''})
                </label>
                {composition.length > 0 && (
                  <button
                    onClick={() => setComposition([])}
                    className="text-xs text-[var(--color-brick)] hover:underline"
                  >
                    Tout effacer
                  </button>
                )}
              </div>

              {composition.length === 0 ? (
                <div className="border-2 border-dashed border-[var(--border)] rounded-lg p-8 text-center">
                  <Plus className="w-12 h-12 mx-auto mb-2 text-[var(--muted-foreground)] opacity-30" />
                  <p className="text-sm text-[var(--muted-foreground)]">
                    Aucun ingrédient ajouté
                  </p>
                  <p className="text-xs text-[var(--muted-foreground)] mt-1">
                    Recherchez et cliquez sur un ingrédient pour l'ajouter
                  </p>
                </div>
              ) : (
                <div className="space-y-2 max-h-[400px] overflow-y-auto">
                  {composition.map((item, index) => (
                    <div
                      key={index}
                      className="flex items-center gap-2 bg-[var(--muted)] p-3 rounded-lg"
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-[var(--foreground)] truncate">
                          {item.nom}
                        </p>
                      </div>

                      <input
                        type="number"
                        value={item.quantite}
                        onChange={(e) =>
                          handleUpdateQuantity(index, parseFloat(e.target.value) || 0)
                        }
                        min="0"
                        step="0.1"
                        className="w-20 px-2 py-1 text-sm bg-[var(--input-background)] border border-[var(--border)] rounded text-[var(--foreground)] text-center [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                      />

                      <select
                        value={item.unite}
                        onChange={(e) => handleUpdateUnit(index, e.target.value)}
                        className="w-20 px-2 py-1 text-sm bg-[var(--input-background)] border border-[var(--border)] rounded text-[var(--foreground)]"
                      >
                        <option value="ml">ml</option>
                        <option value="cl">cl</option>
                        <option value="oz">oz</option>
                        <option value="g">g</option>
                        <option value="kg">kg</option>
                        <option value="unité">unité</option>
                        <option value="dash">dash</option>
                      </select>

                      <button
                        onClick={() => handleRemoveIngredient(index)}
                        className="p-1.5 text-[var(--color-brick)] hover:bg-[var(--color-brick)]/10 rounded transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              {/* Analyse en temps réel */}
              {composition.length > 0 && (
                <div className="bg-[var(--background)] border border-[var(--border)] rounded-lg p-4 space-y-3">
                  <h4 className="text-sm font-semibold text-[var(--foreground)] flex items-center gap-2">
                    <DollarSign className="w-4 h-4 text-[var(--color-steel)]" />
                    Analyse de rentabilité
                  </h4>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-[var(--muted-foreground)] text-xs">Coût total</p>
                      <p className="font-semibold text-[var(--foreground)]">
                        {analysis.coutTotal.toLocaleString("fr-CA", {
                          style: "currency",
                          currency: "CAD",
                        })}
                      </p>
                    </div>

                    <div>
                      <p className="text-[var(--muted-foreground)] text-xs">Prix vente</p>
                      <p className="font-semibold text-[var(--foreground)]">
                        {analysis.prixVente.toLocaleString("fr-CA", {
                          style: "currency",
                          currency: "CAD",
                        })}
                      </p>
                    </div>

                    <div>
                      <p className="text-[var(--muted-foreground)] text-xs">Marge</p>
                      <p
                        className={`font-semibold ${
                          analysis.margePercent >= 50
                            ? "text-[var(--color-olive)]"
                            : analysis.margePercent >= 30
                            ? "text-[var(--color-amber)]"
                            : "text-[var(--color-brick)]"
                        }`}
                      >
                        {analysis.margePercent.toFixed(1)}%
                      </p>
                    </div>

                    <div>
                      <p className="text-[var(--muted-foreground)] text-xs">Profit</p>
                      <p
                        className={`font-semibold ${
                          analysis.margeDollars >= 0
                            ? "text-[var(--color-olive)]"
                            : "text-[var(--color-brick)]"
                        }`}
                      >
                        {analysis.margeDollars.toLocaleString("fr-CA", {
                          style: "currency",
                          currency: "CAD",
                        })}
                      </p>
                    </div>
                  </div>

                  {analysis.ingredientsManquants.length > 0 && (
                    <div className="pt-2 border-t border-[var(--border)]">
                      <p className="text-xs text-[var(--color-amber)] flex items-center gap-1">
                        <AlertCircle className="w-3 h-3" />
                        Certains ingrédients n'ont pas de prix coûtant
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-[var(--border)] bg-[var(--background)]">
          <Button
            variant="secondary"
            size="md"
            onClick={onClose}
          >
            Annuler
          </Button>

          <Button
            variant="primary"
            size="md"
            onClick={handleSave}
            disabled={!hasModifications}
          >
            Sauvegarder la recette
          </Button>
        </div>
      </div>
    </div>
  );
}